import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { Badge } from "../../components/ui/badge";
import { FileDown, Mail, MessageSquare, Copy } from "lucide-react";
import { toast } from "sonner";

const TAB_LABELS = {
  audit: "Audit & Accounting", mca: "MCA / Company Law", income_tax: "Income Tax",
  gst: "GST", rbi_fema: "RBI & FEMA", sebi: "SEBI / Markets",
  valuation: "Valuation", insolvency: "IBC / Insolvency",
  judicial: "Judicial", others: "Others",
};

const critColors = { high: "bg-red-100 text-red-700", medium: "bg-amber-100 text-amber-700", low: "bg-emerald-100 text-emerald-700" };

export default function KipBulletin() {
  const [updates, setUpdates] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [title, setTitle] = useState("VCAJ Weekly Bulletin");
  const [whatsappText, setWhatsappText] = useState("");
  const [emailHtml, setEmailHtml] = useState("");

  useEffect(() => {
    api.get("/kip/updates", { params: { limit: 500 } }).then(({ data }) => setUpdates(data));
  }, []);

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  // Group updates by tab for the selection panel
  const grouped = useMemo(() => {
    const g = {};
    updates.forEach((u) => {
      const t = u.tab || "others";
      g[t] = g[t] || [];
      g[t].push(u);
    });
    return g;
  }, [updates]);

  const selectAllInTab = (tab) => {
    const ids = (grouped[tab] || []).map((u) => u.id);
    const s = new Set(selected);
    const allOn = ids.every((id) => s.has(id));
    if (allOn) ids.forEach((id) => s.delete(id));
    else ids.forEach((id) => s.add(id));
    setSelected(s);
  };

  const generate = async (format) => {
    if (!selected.size) { toast.error("Select at least one update"); return; }
    const ids = Array.from(selected);
    try {
      if (format === "pdf") {
        const { data } = await api.post("/kip/bulletin", { update_ids: ids, title, format: "pdf" }, { responseType: "blob" });
        const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
        const a = document.createElement("a"); a.href = url; a.download = `bulletin.pdf`; a.click(); URL.revokeObjectURL(url);
      } else if (format === "email") {
        const { data } = await api.post("/kip/bulletin", { update_ids: ids, title, format: "email" });
        setEmailHtml(data.html);
      } else if (format === "whatsapp") {
        const { data } = await api.post("/kip/bulletin", { update_ids: ids, title, format: "whatsapp" });
        setWhatsappText(data.text);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="kip-bulletin-page">
      <div>
        <div className="eyebrow">Knowledge Intelligence</div>
        <h1 className="font-sans text-3xl">Bulletin Builder</h1>
        <p className="text-sm text-slate-600 mt-1">Select regulatory updates and generate a client-facing bulletin in PDF, email or WhatsApp format.</p>
      </div>

      <div className="flex gap-3 items-end flex-wrap">
        <div className="flex-1 max-w-md">
          <label className="text-xs text-slate-500">Bulletin Title</label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="bulletin-title"/>
        </div>
        <div className="text-sm text-slate-500 self-end">{selected.size} selected</div>
        <Button onClick={() => generate("pdf")} data-testid="gen-pdf"><FileDown className="h-4 w-4 mr-1"/>PDF</Button>
        <Button variant="outline" onClick={() => generate("email")} data-testid="gen-email"><Mail className="h-4 w-4 mr-1"/>Email HTML</Button>
        <Button variant="outline" onClick={() => generate("whatsapp")} data-testid="gen-whatsapp"><MessageSquare className="h-4 w-4 mr-1"/>WhatsApp Text</Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-3 max-h-[60vh] overflow-y-auto" data-testid="bulletin-selector">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Select Updates by Section</div>
          <div className="space-y-4">
            {Object.keys(grouped).length === 0 && <div className="text-sm text-slate-400 p-4 text-center">No updates yet. Seed samples or refresh feeds first.</div>}
            {Object.entries(grouped).map(([tab, items]) => {
              const ids = items.map((u) => u.id);
              const selectedCount = ids.filter((id) => selected.has(id)).length;
              return (
                <div key={tab} className="border border-slate-200 rounded" data-testid={`bulletin-section-${tab}`}>
                  <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
                    <div className="font-semibold text-sm text-slate-900">{TAB_LABELS[tab] || tab} <span className="text-xs text-slate-500 ml-1">({selectedCount}/{items.length})</span></div>
                    <Button size="sm" variant="ghost" onClick={() => selectAllInTab(tab)} data-testid={`bulletin-select-all-${tab}`}>
                      {selectedCount === items.length ? "Deselect all" : "Select all"}
                    </Button>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {items.map((u) => (
                      <div key={u.id} className="flex items-start gap-3 p-2 hover:bg-slate-50 cursor-pointer" onClick={() => toggle(u.id)} data-testid={`bulletin-row-${u.id}`}>
                        <Checkbox checked={selected.has(u.id)} onCheckedChange={() => toggle(u.id)}/>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Badge className={critColors[u.criticality]}>{u.criticality}</Badge>
                            <span className="text-xs text-slate-500">{u.source} · {u.issued_on}</span>
                          </div>
                          <div className="text-sm font-medium text-slate-900">{u.title}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <div className="space-y-3">
          {whatsappText && (
            <Card className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold">WhatsApp Text</div>
                <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(whatsappText); toast.success("Copied"); }} data-testid="copy-whatsapp"><Copy className="h-3 w-3 mr-1"/>Copy</Button>
              </div>
              <pre className="text-xs whitespace-pre-wrap bg-slate-50 p-3 rounded border border-slate-200 max-h-72 overflow-y-auto">{whatsappText}</pre>
            </Card>
          )}
          {emailHtml && (
            <Card className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold">Email HTML Preview</div>
                <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(emailHtml); toast.success("Copied HTML"); }} data-testid="copy-email"><Copy className="h-3 w-3 mr-1"/>Copy HTML</Button>
              </div>
              <div className="border border-slate-200 rounded bg-white p-3 max-h-72 overflow-y-auto" dangerouslySetInnerHTML={{ __html: emailHtml }}/>
            </Card>
          )}
          {!whatsappText && !emailHtml && <Card className="p-8 text-center text-slate-400 text-sm">Select updates and click a format to generate.</Card>}
        </div>
      </div>
    </div>
  );
}
